import { useEffect, useLayoutEffect, useRef } from "react";
import { composerLimitState } from "../livingRoom.js";
// **W4-7's MODULE, NOT A SECOND ONE.** `applyLiveView` is the grow-and-scroll mechanism this house
// already built for her dictation ruling — the clamp, the overflow flip, the `height = "auto"` reset
// that makes shrinking work, and the scroll that keeps the newest words visible once the box has
// stopped growing. The message composer supplies its own CEILING and nothing else.
import { applyLiveView, messageComposeLimits } from "../lib/live-view";

// **THE ONE BOX A MEMBER WRITES INTO** (her order, 2026-08-07).
//
// Her report: *"The inbox write-a-message box is a single row on mobile and desktop, long messages
// scroll away out of sight as she types."* Her ruling: it GROWS, multi-line, tall enough to show
// everything written, up to roughly a third of the screen, past which the box scrolls internally;
// **Enter makes a new line and the button sends**; a 2,000-character cap where typing stops and a
// small red line appears in house language, shown only NEAR the limit.
//
// **IT IS ONE COMPONENT BECAUSE HER CONSOLIDATION LAW SAYS SO, AND MEASURING FIRST IS WHY THIS ROUND
// IS A MOVE RATHER THAN AN EDIT (R25).** There were TWO single-row `<input>`s — one in
// `LivingRoomInbox.tsx` for DMs and groups, one in `LivingRoom.tsx` for comments and replies — each
// with its own `maxLength`, its own Enter handling and its own picker wiring. Growing "the composer"
// would have grown one of them and left the other exactly as she reported it. Both call this now, so
// **the Living Room comment box inherits every clause by existing**, which is her clause 3.
//
// **`<textarea>` AND NOT AN `<input>`, WHICH IS THE WHOLE ROUND.** An `<input>` is a single line by
// definition — it cannot hold a newline, cannot wrap, and cannot be told to grow. That is why her
// message scrolled sideways out of sight rather than down the screen.
//
// **THE TWO DECISIONS ARE PURE AND NEITHER IS DECLARED IN THIS FILE.** The CAP law is
// `composerLimitState` in `src/livingRoom.js`; the GROW law is `applyLiveView` + `messageComposeLimits`
// in `src/lib/live-view.ts`, which is W4-7s module and already had every part of it. A gate RUNS both
// over sizes no browser here will produce. What is left in this file is the part that genuinely needs
// a rendered element: `scrollHeight` is a fact about a painted box, not arithmetic.

export type MessageComposerProps = {
  value: string;
  onChange: (next: string, caret: number) => void;
  onBlur?: () => void;
  max: number;
  placeholder: string;
  // The picker is the caller's — it anchors to this box and each surface has its own roster.
  inputRef?: { current: HTMLTextAreaElement | null };
  disabled?: boolean;
  // Whatever the surface puts inside the field beside the text (the picture control, the picker).
  before?: React.ReactNode;
  after?: React.ReactNode;
  // Told after every grow, so the surface can keep its own log pinned to the newest message.
  onResized?: () => void;
};

export function MessageComposer({
  value, onChange, onBlur, max, placeholder, inputRef, disabled, before, after, onResized,
}: MessageComposerProps) {
  const own = useRef<HTMLTextAreaElement | null>(null);
  const el = inputRef || own;
  const limit = composerLimitState({ text: value, max });

  // **THE GROW, ON LAYOUT RATHER THAN ON PAINT.** `useLayoutEffect` runs before the browser draws,
  // so the box is already the right height in the frame her keystroke lands in — with `useEffect`
  // the row visibly jumps one frame late on every character, which reads as a slow phone.
  const grow = () => {
    const node = el.current;
    if (!node) return;
    applyLiveView(node, messageComposeLimits(typeof window === "undefined" ? 0 : window.innerHeight));
    // **AND THE SURFACE IS TOLD, WHICH IS HER CLAUSE 4.** A composer that grows inside a flex column
    // shrinks the message log above it — but the log keeps its old `scrollTop`, so the newest message
    // slides up out of the clip and she is left typing at a thread whose last line she can no longer
    // see. That is *covering* by another route. The caller re-pins its own log (through
    // `alreadyAtLatest`, so a member who deliberately scrolled UP is still not yanked back down).
    onResized?.();
  };
  useLayoutEffect(grow, [value, el]);

  // A phone rotating, or a desktop window resized, changes the ceiling — a third of the screen is a
  // different number then, and a box left at the old one either wastes space or covers the thread.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    window.addEventListener("resize", grow);
    return () => window.removeEventListener("resize", grow);
  }, [el]);

  return (
    <div className="lr-reply__field">
      {before}
      <textarea
        ref={el as React.RefObject<HTMLTextAreaElement>}
        className="lr-composer__growbox"
        value={value}
        rows={1}
        placeholder={placeholder}
        aria-label={placeholder}
        disabled={disabled}
        // **THE CAP IS ON THE ELEMENT AND ALSO IN THE LAW, AND BOTH ARE ON PURPOSE.** `maxLength` is
        // what makes typing STOP — her clause, and the browser does it without a re-render — while
        // `composerLimitState` is what decides whether she is TOLD. Only one of those two can be
        // checked without a browser, which is why the telling half is the pure one.
        maxLength={max}
        // **THE CAP IS CLAMPED IN THE LAW AND NOT ONLY ON THE ELEMENT.** `maxLength` stops a
        // person TYPING and stops nothing else — a paste from a browser that ignores it, a dictation
        // insert, an autofill, or any programmatic set arrives at full length. The walk found this
        // by putting 2,500 characters in a box capped at 2,000. One clamp, at the one door every
        // path goes through.
        onChange={(e) => onChange(String(e.target.value).slice(0, max), e.target.selectionStart || 0)}
        onBlur={onBlur}
        // **NO `onKeyDown` SENDING. HER RULING: "Enter creates a new line naturally, send is the send
        // button."** A textarea does that by doing nothing at all, so the correct implementation of
        // her clause is the ABSENCE of a handler — which is exactly the kind of thing a presence
        // check cannot see, so the fixture asserts the absence against both call sites.
      />
      {after}
      {/* HER RED LINE, and it renders ONLY at the cap. `nearLimit` gets the quiet count instead —
          "only near the limit, not always" is two states, not one (R18), and a box that showed a
          counter from the first character would be the clutter she is asking me to remove. */}
      {limit.atLimit ? (
        <p className="lr-composer__max" role="status">{limit.notice}</p>
      ) : limit.nearLimit ? (
        <p className="lr-composer__count">{limit.remaining}</p>
      ) : null}
    </div>
  );
}
